import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  X,
  FileText,
  Users,
  UserCog,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  FolderTree,
  Box,
  Images,
  ClipboardList,
  ArrowLeftRight,
  Lock,
} from "lucide-react";
import { useAdminAuth } from "../../services/admin/AdminAuthServices";
import { useComingSoon } from "../../utils/useComingSoon";


export default function AdminSidebar({ onClose, collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const location = useLocation(); // Get the current location
  const { admin, adminLogout } = useAdminAuth();
  const { showComingSoon } = useComingSoon();
  const [activeItem, setActiveItem] = useState("");

  const navigationItems = useMemo(() => [
    { name: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },
    {
      name: "Inventory",
      icon: Package,
      isParent: true,
      children: [
        {
          name: "Categories",
          icon: FolderTree,
          path: "/admin/inventory/categories",
        },
        { name: "Products", icon: Box, path: "/admin/inventory/products" },
        {
          name: "Transactions",
          icon: ArrowLeftRight,
          // path: "/admin/inventory/transactions",
        },
      ],
    },
    { name: "Catalog", icon: FileText, path: "/admin/catalog" },
    { name: "Gallery", icon: Images, path: "/admin/gallery" },
    { name: "Invoices", icon: FileText, path: "/admin/invoices" },
    { name: "Customers", icon: Users, path: "/admin/customers" },
    { name: "Admins", icon: UserCog, path: "/admin/admins/", minRole: "super_admin" },
    { name: "Audit Logs", icon: ClipboardList, path: "/admin/audit-logs", minRole: "admin" },
    { name: "Settings", icon: Settings, path: "" },
    { name: "Change Password", icon: Lock, path: "/admin/change-password/" },
  ], []);

  // Role rank helper — matches backend ROLE_RANK
  const ROLE_RANK = { staff: 1, admin: 2, super_admin: 3 };
  const adminRank = admin?.is_system_default ? 4 : (ROLE_RANK[admin?.role] || 0);

  // Filter nav items based on caller's role
  const visibleItems = navigationItems.filter(item => {
    if (!item.minRole) return true;
    const required = ROLE_RANK[item.minRole] || 0;
    return adminRank >= required;
  });

  // Update active item based on the current route
  useEffect(() => {
    const findActiveItem = (items, path) => {
      for (const item of items) {
        if (item.path) {
          // Exact match for dashboard to avoid greedy matching
          if (item.path === "/admin/dashboard" || item.path === "/") {
            if (path === item.path) return item.name;
          } else if (path.startsWith(item.path)) {
            // Check if there's a child that's a better match first
            if (item.children) {
              const childActive = findActiveItem(item.children, path);
              if (childActive) return childActive;
            }
            return item.name;
          }
        }
        if (item.children) {
          const childActive = findActiveItem(item.children, path);
          if (childActive) return childActive;
        }
      }
      return null;
    };

    const activeName = findActiveItem(navigationItems, location.pathname);
    if (activeName) {
      setActiveItem(activeName);
    }
  }, [location.pathname, navigationItems]);

  const handleItemClick = (item) => {
    setActiveItem(item.name);

    // If the item has no path, show coming soon modal
    if (!item.path) {
      showComingSoon({
        title: `${item.name} Coming Soon!`,
        message: `We're building the ${item.name} feature. It will be available in the next update. Stay tuned!`,
      });
      return;
    }

    // Navigation is handled by Link component now

    if (onClose && typeof window !== "undefined" && window.innerWidth < 1024) {
      onClose();
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout(navigate);
    } catch (error) {
      console.error("Logout failed:", error);
      localStorage.removeItem("admin");
      localStorage.removeItem("admin_token");
      navigate("/admin/login", { replace: true });
    }
  };

  return (
    <div
      className={`${collapsed ? "w-20" : "w-64"
        } bg-white dark:bg-[#1A1A1A] flex-shrink-0 flex flex-col h-full border-r border-[#E5E7EB] dark:border-[#2A2A2A] transition-all duration-300`}
    >
      {/* Brand Logo */}
      <div className="p-6 flex items-center justify-between border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
        {!collapsed && (
          <div className="flex items-center gap-3">
            <div onClick={() => navigate("/admin/dashboard/")} className="w-10 h-10 bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-xl flex items-center justify-center shadow-lg">
              <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" loading="eager" />
            </div>
            <div>
              <h2 className="font-bold text-[#111111] dark:text-white text-sm font-poppins">
                St. Stephen's
              </h2>
              <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-inter">
                Rentals & Events
              </p>
              <p className="text-[11px] text-[#62397f] dark:text-[#A78BFA] font-medium mt-1 truncate max-w-[140px]">
                {admin?.full_name || "Admin"}
              </p>
              <p className="text-[10px] text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wide truncate max-w-[140px]">
                {admin?.role?.replace(/_/g, " ") || "Admin Role"}
              </p>
            </div>
            {/* Mobile Close Button */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-[#666666] dark:text-[#A0A0A0] hover:text-[#111111] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#2A2A2A] rounded-lg transition-colors ml-auto"
            >
              <X size={20} />
            </button>
          </div>
        )}
        {collapsed && (
          <div className="w-10 h-10 flex items-center justify-center mx-auto">
            <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" loading="eager" />
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="space-y-1">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeItem === item.name;

            if (item.isParent) {
              return (
                <div key={item.name} className="space-y-1">
                  {/* Parent Item */}
                  <div
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${collapsed ? "justify-center" : ""
                      } text-[#666666] dark:text-[#A0A0A0]`}
                    title={collapsed ? item.name : ""}
                  >
                    <Icon size={20} strokeWidth={2} />
                    {!collapsed && (
                      <span className="font-medium text-sm font-inter">
                        {item.name}
                      </span>
                    )}
                  </div>

                  {/* Always visible children */}
                  {!collapsed && item.children && (
                    <div className="ml-3 pl-4 border-l-2 border-[#E5E7EB] dark:border-[#2A2A2A] space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const isChildActive = activeItem === child.name;

                        // Conditional rendering based on path existence for children
                        if (child.path) {
                          return (
                            <Link
                              key={child.name}
                              to={child.path}
                              onClick={() => handleItemClick(child)}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${isChildActive
                                ? "bg-gradient-to-r from-[#F5F3FF] to-[#FAF5FF] dark:from-[#2A2440] dark:to-[#2A2440] border border-[#62397f] dark:border-[#7C3AED] text-[#62397f] dark:text-[#A78BFA]"
                                : "text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white"
                                }`}
                            >
                              <ChildIcon
                                size={18}
                                className={
                                  isChildActive
                                    ? "text-[#62397f] dark:text-[#A78BFA]"
                                    : ""
                                }
                                strokeWidth={isChildActive ? 2.5 : 2}
                              />
                              <span
                                className={`text-sm font-inter ${isChildActive ? "font-semibold" : "font-medium"}`}
                              >
                                {child.name}
                              </span>
                            </Link>
                          );
                        }

                        return (
                          <button
                            key={child.name}
                            onClick={() => handleItemClick(child)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${isChildActive
                              ? "bg-gradient-to-r from-[#F5F3FF] to-[#FAF5FF] dark:from-[#2A2440] dark:to-[#2A2440] border border-[#62397f] dark:border-[#7C3AED] text-[#62397f] dark:text-[#A78BFA]"
                              : "text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white"
                              }`}
                          >
                            <ChildIcon
                              size={18}
                              className={
                                isChildActive
                                  ? "text-[#62397f] dark:text-[#A78BFA]"
                                  : ""
                              }
                              strokeWidth={isChildActive ? 2.5 : 2}
                            />
                            <span
                              className={`text-sm font-inter ${isChildActive ? "font-semibold" : "font-medium"}`}
                            >
                              {child.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Regular menu items
            if (item.path) {
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => handleItemClick(item)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${isActive
                    ? "bg-gradient-to-r from-[#F5F3FF] to-[#FAF5FF] dark:from-[#2A2440] dark:to-[#2A2440] border border-[#62397f] dark:border-[#7C3AED] text-[#62397f] dark:text-[#A78BFA]"
                    : "text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white active:bg-[#F3F4F6] dark:active:bg-[#2A2A2A] active:scale-[0.98]"
                    }`}
                  title={collapsed ? item.name : ""}
                >
                  <Icon
                    size={20}
                    className={
                      isActive ? "text-[#62397f] dark:text-[#A78BFA]" : ""
                    }
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                  {!collapsed && (
                    <span
                      className={`font-medium text-sm font-inter ${isActive ? "font-semibold" : ""}`}
                    >
                      {item.name}
                    </span>
                  )}
                </Link>
              );
            }

            return (
              <button
                key={item.name}
                onClick={() => handleItemClick(item)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${isActive
                  ? "bg-gradient-to-r from-[#F5F3FF] to-[#FAF5FF] dark:from-[#2A2440] dark:to-[#2A2440] border border-[#62397f] dark:border-[#7C3AED] text-[#62397f] dark:text-[#A78BFA]"
                  : "text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white active:bg-[#F3F4F6] dark:active:bg-[#2A2A2A] active:scale-[0.98]"
                  }`}
                title={collapsed ? item.name : ""}
              >
                <Icon
                  size={20}
                  className={
                    isActive ? "text-[#62397f] dark:text-[#A78BFA]" : ""
                  }
                  strokeWidth={isActive ? 2.5 : 2}
                />
                {!collapsed && (
                  <span
                    className={`font-medium text-sm font-inter ${isActive ? "font-semibold" : ""}`}
                  >
                    {item.name}
                  </span>
                )}
              </button>
            );
          })}


          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 active:scale-[0.98] mt-2"
            title={collapsed ? "Logout" : ""}
          >
            <LogOut size={20} strokeWidth={2} />
            {!collapsed && (
              <span className="font-medium text-sm font-inter">Logout</span>
            )}
          </button>
        </div>
      </nav>

      {/* Collapse Toggle - Desktop Only */}
      <div className="hidden lg:block p-3 border-t border-[#E5E7EB] dark:border-[#2A2A2A]">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white transition-all duration-200 active:scale-95"
        >
          {collapsed ? (
            <ChevronRight size={18} />
          ) : (
            <>
              <ChevronLeft size={18} />
              <span className="text-sm font-medium font-inter">Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
